// Browser specific logic. Until browser.ts this doesn't need to have the same
// signature as server.ts. Use this for setup functions and other logic that
// only needs to run in the browser. This file should only be imported by
// src/browser.ts or other browser-only files.
// See: docs/isomorphic-imports.md

import { getContext } from 'svelte';
import { get } from 'svelte/store';
import {
    isCommerceModalOpen,
    needsGDPR as needsGDPRStore,
} from '@amp/web-app-components/src/stores/needs-gdpr';
import type { Logger, LoggerFactory } from '@amp/web-apps-logger';
import {
    HostedAppSubdomain,
    type CommerceKitApi,
} from '@amp-ui-commerce/commercekit';
import type { Config } from '@amp-ui-commerce/commercekit/dist/types/packages/commercekit/src/config';
import { JWT } from '~/config/media-api';
import type { Jet } from '~/jet';
import {
    type CommerceState,
    type CommerceStore,
    createCommerceStore,
} from '~/utils/commerce/state';
import type { Playback } from '~/utils/playback/browser-only';
import {
    SubscriberType,
    getMusicSubscriptionStatus,
    makeUpsellOfferDetails,
} from '~/utils/subscription';
import { type User } from '~/utils/user';
import { getUserInfo } from '~/utils/user/browser-only';
import { COMMERCE_ROUTE_MAP, CommerceRoute, EMBEDDED_ROUTES } from './models';
import { MODAL_ID } from '@amp/web-app-components/src/components/Commerce/ModalContainer.svelte';
import {
    CommerceModalActionOperationType,
    makeCommerceModalAction,
} from '~/actions/commerce-modal-action';
import { makeInvalidationAction } from '~/jet/models';
import { makeSignOutAction } from '~/actions/sign-out-action';
import { fullScreenPlayerState } from '~/stores/full-screen-player';
import { debounce } from '@amp/web-app-components/src/utils/debounce';

const isDevelopment = process.env.NODE_ENV === 'development';
const CONTEXT_NAME = 'commerce';
const COMMERCE_PRODUCT = 'classical';

const SUBSCRIPTION_INVALIDATION_DEBOUNCE = 2000;

// Create a shared debounced function for subscription status invalidation
const performSubscriptionInvalidation = debounce((jet) => {
    jet.perform(
        makeInvalidationAction({
            kind: 'subscriptionStatusChange',
        }),
    );
}, SUBSCRIPTION_INVALIDATION_DEBOUNCE);

export const ROUTES_FROM_TO_REFRESH_USER = [
    'billing-info',
    'subscribe-success',
    'subscription-confirmation',
    'create-account',
];

export const ROUTES_FROM_TO_TRIGGER_GDPR = [
    'buy-with-billing', // `buy-with-billing` is what Music receives
    'buy-v2', // `buy-v2` is new for Classical
];

export interface LocaleInfo {
    language: string;
    storefront: string;
}

export interface CommerceModalParams {
    iso2code?: string;
    locale?: string;
}

export class Commerce {
    private readonly manager: Promise<CommerceKitApi>;
    private playback: Playback;
    private jet: Jet;
    private locale: LocaleInfo;

    private readonly stateModifiers: CommerceStore['internal'];
    readonly state: CommerceStore['external'];

    private constructor(
        manager: Promise<CommerceKitApi>,
        commerceStore: CommerceStore,
        playback: Playback,
        jet: Jet,
        locale: LocaleInfo,
    ) {
        this.manager = manager;
        this.stateModifiers = commerceStore.internal;
        this.state = commerceStore.external;
        this.playback = playback;
        this.jet = jet;
        this.locale = locale;
    }

    /**
     * Setup Commerce for Media App & also initiate Sign In Iframe if not authenticated
     * @returns success A promise that rejects if commerce manager is unable to setup properly
     */
    static setup(
        context: Map<string, unknown>,
        loggerFactory: LoggerFactory,
        playback: Playback,
        jet: Jet,
        locale: LocaleInfo,
    ): Commerce {
        const commerceInfo = createCommerceStore();
        const log: Logger = loggerFactory.loggerFor('Commerce');
        const commerceInstance = new Commerce(
            (async () => {
                let commerceKitInstance: CommerceKitApi;
                const { AuthType, EventName, setupCommerceKit } = await import(
                    '@amp-ui-commerce/commercekit'
                );

                const modalOptions = {
                    containerSelector: `#${MODAL_ID}`,

                    onModalReadyToBeShown: () => {
                        isCommerceModalOpen.set(true);
                    },
                };
                const onDidSignIn = () => {
                    updateAuthorizedUser(playback, jet, commerceInfo);
                    // if (shouldRedirectOnSignIn()) {
                    //     jet.perform(
                    //         makeNavigationAction(
                    //             browsePageIntent({
                    //                 storefront: null,
                    //                 language: null,
                    //             }),
                    //             BROWSE_ID.type,
                    //             '/browse',
                    //         ),
                    //     );
                    // }
                };
                const onDidGdprAcknowledge = () => {
                    needsGDPRStore.set(false);
                };
                const onCloseModal = async (_sender, ...params) => {
                    isCommerceModalOpen.set(false);
                    log.info('Closing CommerceKit Modal: ', params);

                    const [{ data }] = params;

                    // If a user did not acknowledge the GDPR and closed the Modal, forcefully logout
                    // only if the DID_CLOSE event is from 'privacy' modal and not from others
                    const needsGDPR = get(needsGDPRStore);
                    if (needsGDPR && data?.from === 'privacy') {
                        jet.perform(makeSignOutAction());
                        needsGDPRStore.set(false);
                    }

                    // If a user closes the modal before completing the payment info flow
                    // when creating a new account, we make sure to trigger the GDPR modal
                    // so the user is clear what their next steps are
                    else if (
                        needsGDPR &&
                        ROUTES_FROM_TO_TRIGGER_GDPR.includes(data?.from)
                    ) {
                        jet.perform(
                            makeCommerceModalAction({
                                type: CommerceModalActionOperationType.Open,
                                activePath: CommerceRoute.gdpr,
                            }),
                        );
                    }

                    if (
                        !data ||
                        ROUTES_FROM_TO_REFRESH_USER.includes(data?.from)
                    ) {
                        updateAuthorizedUser(playback, jet, commerceInfo);
                    }
                };

                const commerceSetupConfig: Config = {
                    authOptions: { type: AuthType.DefaultWeb },
                    devToken: JWT,
                    fetchOptions: {
                        shouldEnableFetchProxy: true,
                    },
                    hostedAppMetadata: {
                        subdomain: HostedAppSubdomain.Music,
                    },
                    locale: locale.language,
                    // Use `verbose` for more logs
                    logLevel: isDevelopment ? 'warning' : 'off',
                    modalOptions,
                    product: COMMERCE_PRODUCT,
                    storefront: locale.storefront,
                    useRelativeIframeSrc: !isDevelopment,
                };

                loadUserAndSubscriptionsAsync(playback, commerceInfo, log, jet);

                listenToMusicKitCommerceEvents(
                    playback,
                    jet,
                    commerceInfo,
                    locale,
                );

                // Setup and get Commerce Kit Instance
                commerceKitInstance = setupCommerceKit(commerceSetupConfig);
                commerceKitInstance.eventEmitter.addListener(
                    EventName.DidSignIn,
                    onDidSignIn,
                );
                commerceKitInstance.eventEmitter.addListener(
                    EventName.DidGdprAcknowledge,
                    onDidGdprAcknowledge,
                );
                commerceKitInstance.eventEmitter.addListener(
                    EventName.ModalDidClose,
                    onCloseModal,
                );

                commerceInfo.internal.setup();
                return commerceKitInstance;
            })(),
            commerceInfo,
            playback,
            jet,
            locale,
        );

        context.set(CONTEXT_NAME, commerceInstance);

        return commerceInstance;
    }

    async open(
        path: CommerceRoute,
        additionalCommerceParams: CommerceModalParams = {},
    ) {
        // Append the rest of queryParams in the url
        const urlSearchParams = new URL(window.location.href).searchParams;
        const urlQueryParams = Object.fromEntries(urlSearchParams);
        const manager = await this.manager;

        // Set defaults query params for modal
        // Add commerce params
        const queryParams = {
            ...additionalCommerceParams,
            ...urlQueryParams,
        };

        const routeName = COMMERCE_ROUTE_MAP[path];
        const shouldOpenEmbedded = EMBEDDED_ROUTES.includes(path) ?? false;
        if (shouldOpenEmbedded) {
            // return manager.createIframe(routeName, {
            //     queryParams,
            // });
        } else if (path === CommerceRoute.authenticate) {
            // Convenience method to open commerce kit authenticate route
            return manager.authenticate({ queryParams });
        } else {
            return manager.openModal(routeName, { queryParams });
        }
    }

    async logout() {
        const manager = await this.manager;
        return manager.logout();
    }

    async refreshUser(): Promise<void> {
        await this.playback.me();
    }
}

/**
 * Method to load User Info and Subscriptions Offers in Parallel
 * @param playback
 * @param userInfo
 * @param log
 */
function loadUserAndSubscriptionsAsync(
    playback: Playback,
    commerceInfo: CommerceStore,
    log: Logger,
    jet: Jet,
) {
    getUserInfo(playback)
        .then((user: User) => {
            commerceInfo.internal.setUser(user);
            jet.updateUserInfo(user);

            const isSubscribed =
                user?.subscriberType === SubscriberType.Premium;

            // Only for signed in users try and fetch social profile
            if (isSubscribed) {
                fetchSubscriptionStatus(commerceInfo);

                performSubscriptionInvalidation(jet);
            }
        })
        .catch((e) => {
            log.error('error while getting userInfo:', e);
        });

    playback
        .musicSubscriptionOffers()
        .then((offers) => {
            const upsellOfferDetails = makeUpsellOfferDetails(offers);
            commerceInfo.internal.setUpsellOfferDetails(upsellOfferDetails);
        })
        .catch((e) => {
            log.error('Error in getting Subscription Offers: ', e);
            // Default Empty Offer to be set to ensure that Default Offer is actually rendered for Listen now
            // This is for Local Dev mostly or rare cases when Media API for Subscription Offers is completely failing
            commerceInfo.internal.setUpsellOfferDetails(
                makeUpsellOfferDetails([]),
            );
        });
}

function listenToMusicKitCommerceEvents(
    playback: Playback,
    jet: Jet,
    commerceInfo: CommerceStore,
    locale: LocaleInfo,
) {
    addEligibleForSubscribeViewEvent(playback, jet);

    addNeedsGDPRDidChangeEvent(playback, jet);

    addAuthorizationStatusDidChangeEvent(playback, jet, commerceInfo, locale);
}

function addEligibleForSubscribeViewEvent(playback: Playback, jet: Jet) {
    playback.addEventListener('eligibleForSubscribeView', ({ item }) => {
        jet.perform(
            makeCommerceModalAction({
                type: CommerceModalActionOperationType.Open,
                activePath: CommerceRoute.subscribe,
                modalParams: {
                    contentId: item?.id,
                    contentType: item?.attributes?.playParams?.kind,
                },
            }),
        );
    });
}

function addNeedsGDPRDidChangeEvent(playback: Playback, jet: Jet) {
    playback.addEventListener('needsGDPRDidChange', ({ needsGDPR }) => {
        if (needsGDPR) {
            needsGDPRStore.set(needsGDPR);

            jet.perform(
                makeCommerceModalAction({
                    type: CommerceModalActionOperationType.Open,
                    activePath: CommerceRoute.gdpr,
                }),
            );
        }
    });
}

function addAuthorizationStatusDidChangeEvent(
    playback: Playback,
    jet: Jet,
    commerceInfo: CommerceStore,
    locale: LocaleInfo,
) {
    playback.addEventListener(
        'authorizationStatusDidChange',
        async ({ authorizationStatus }) => {
            // TODO: See if Musickit can expose AuthorizationStatus as a standalone enum
            if (
                authorizationStatus === 3 &&
                get(commerceInfo.external)?.user === null
            ) {
                // AUTHORIZED - 3
                await updateAuthorizedUser(playback, jet, commerceInfo);

                playback.setPreviewOnlyBasedOnSF(locale.storefront);
            }
        },
    );
}

async function updateAuthorizedUser(
    playback: Playback,
    jet: Jet,
    commerceInfo: CommerceStore,
) {
    // Getting Musickit to Update the Cookie post Auth is Complete
    playback.updateUserTokenFromStorage();
    // Update now playing state on login
    playback.clearQueue();
    fullScreenPlayerState.reset();
    // Setting Svelte User Store to Authenticated status
    const updatedUser = await getUserInfo(playback, true);

    if (updatedUser) {
        commerceInfo.internal.setUser(updatedUser);
        jet.updateUserInfo(updatedUser);
        performSubscriptionInvalidation(jet);
        fetchSubscriptionStatus(commerceInfo);
    }
}

async function fetchSubscriptionStatus(commerceInfo: CommerceStore) {
    const subscriptionStatus = await getMusicSubscriptionStatus();
    if (subscriptionStatus) {
        commerceInfo.internal.setUser(subscriptionStatus?.account ?? null);

        commerceInfo.internal.setUserSubscriptionStatus(
            subscriptionStatus.music,
        );
    }
}

export function getCommerce(): {
    state: CommerceState;
    open: Function;
    refreshUser: () => Promise<void>;
} {
    const commerceInstance = getContext(CONTEXT_NAME) as Commerce | undefined;
    if (!commerceInstance) {
        throw new Error('getCommerce called before Commerce.setup');
    }
    return commerceInstance;
}
